import { useCallback } from "react";
import { Form } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Toggleable } from "../../../hook/useToggle";
import { Action } from "../../../types/reducer-action";
import BusyButton from "../../common/busy-button";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import { AddIcon, CloseIcon } from "../../common/icons";
import './style.scss';
import useUserApi from "./useUserApi";
import UserInfoPanel from "./user-info-panel";
import { useUserDispatch, useUserState } from "./user-manager-context";
import UserRolesPanel from "./user-roles-panel";
import confirm from '../../common/confirm';

const NewUserModal = (props: Toggleable) => {
  const { isOpen, toggle } = props;

  const { form } = useUserState();
  const dispatch = useUserDispatch();

  const { createUserProfile, checkUserEmail, busy } = useUserApi();

  const validate = useCallback(async () => {
    let ok = true;

    if (!form.individualId) {
      dispatch(new Action(DataEntryEvent.AddErrors,
        { individualName: 'Unknown user.' }
      ));
      ok = false;
    }

    if (!form.assignment) {
      dispatch(new Action(DataEntryEvent.AddErrors,
        { individualName: 'The user does not have an active assignment.' }
      ));
      ok = false;
    }

    if (!form?.assignedRoles || form.assignedRoles.length === 0) {
      dispatch(new Action(DataEntryEvent.AddErrors,
        { assignedRoles: 'At least one role is required to be added.' }
      ));
      ok = false;
    }

    return checkUserEmail(form.email).then(valid => ok && valid);
  }, [form]);

  const save = useCallback(() => {
    createUserProfile(form)
      .then(ok => ok && toggle())
      .catch((e: Error) => {
        // this is a one in a million race condition where 2 different users are trying to create the same OMS user at the same time
        // the type ahead search will already filter out existing OMS users, but in the rare case that 2 users performed the search
        // BEFORE the user account was created, then user A finished making the account, this error will happen when user B attempts to make the account
        confirm({
          title: 'Unable to create user',
          content: e.message,
          okText: '', // hide OK button
          cancelText: 'OK',
          titleCenter: true,
          bodyCenter: true
        });
      })
  }, [form]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    validate().then(ok => ok && save());
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="xl" scrollable autoFocus={false}>
      <ModalHeader toggle={toggle} tag={'h2'}>Create New User</ModalHeader>
      <ModalBody>
        <Form id="new_user_form" onSubmit={handleSubmit} className="oms-container">
          <fieldset className="oms-frame p-3">
            <legend>User Details (Required Fields)</legend>
            <UserInfoPanel autoFocus={true} />
          </fieldset>

          <fieldset className="oms-frame p-3">
            <legend>User Roles</legend>
            <UserRolesPanel />
          </fieldset>
        </Form>
      </ModalBody>
      <ModalFooter>
        <BusyButton form="new_user_form" type="submit"
          aria-label="Create" color="primary"
          value="Create" busy={busy}>
          <AddIcon />
        </BusyButton>

        <Button color="secondary" onClick={toggle} disabled={busy}>
          <CloseIcon /> Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default NewUserModal;
